import type {
  Board, Cell, Pos, PlacedEmitter, InventoryEmitter,
  EmitterShape, EmitterType, DifficultyTier, Puzzle,
} from "../types.ts";
import { posKey } from "../types.ts";
import {
  createBoard, boardRows, boardCols, getCell, setCell,
} from "./grid.ts";
import { computeSignalMap, evaluateClues, getArms, distinctRotations } from "./signal.ts";
import { findSolutions } from "./solver.ts";
import { mulberry32, randInt, randPick, shuffle } from "./prng.ts";

// ── Difficulty tiers ──

export const DIFFICULTY_TIERS: Record<string, DifficultyTier> = {
  easy: {
    name: "Easy",
    rows: 5, cols: 5,
    minRange: 1, maxRange: 2,
    shapes: ["single"],
    walls: false, blind: false,
    emitterCount: 4, fixedRatio: 0.5,
    clueCount: 8, parTimeMs: 2 * 60 * 1000,
  },
  medium: {
    name: "Medium",
    rows: 6, cols: 6,
    minRange: 1, maxRange: 3,
    shapes: ["single", "L"],
    walls: true, blind: false,
    emitterCount: 5, fixedRatio: 0.33,
    clueCount: 8, parTimeMs: 3 * 60 * 1000,
  },
  hard: {
    name: "Hard",
    rows: 7, cols: 7,
    minRange: 1, maxRange: 3,
    shapes: ["single", "L", "line"],
    walls: true, blind: false,
    emitterCount: 6, fixedRatio: 0.25,
    clueCount: 9, parTimeMs: 5 * 60 * 1000,
  },
  expert: {
    name: "Expert",
    rows: 8, cols: 8,
    minRange: 1, maxRange: 4,
    shapes: ["single", "L", "cross", "line"],
    walls: true, blind: true,
    emitterCount: 7, fixedRatio: 0.2,
    clueCount: 10, parTimeMs: 7 * 60 * 1000,
  },
};

// ── Puzzle generation ──

let idCounter = 0;
function nextId(prefix: string): string {
  return `${prefix}-${++idCounter}`;
}

/**
 * Generate a puzzle by forward construction:
 * 1. Create empty board, optionally place walls
 * 2. Place all emitters randomly → this is the solution
 * 3. Compute signal map from solution
 * 4. Select clue cells
 * 5. Partition emitters into fixed and inventory
 * 6. Verify unique solution via solver
 * 7. If not unique, promote inventory→fixed or add clue, re-check
 */
export function generatePuzzle(seed: number, tier: DifficultyTier): Puzzle {
  const rng = mulberry32(seed);
  idCounter = 0;

  for (let attempt = 0; attempt < 50; attempt++) {
    const result = tryGeneratePuzzle(rng, tier);
    if (result) return result;
  }

  // Fallback: generate a trivially small puzzle
  return generateFallbackPuzzle(rng, tier);
}

function tryGeneratePuzzle(
  rng: () => number,
  tier: DifficultyTier
): Puzzle | null {
  const { rows, cols } = tier;
  let board = createBoard(rows, cols);

  // Step 1: Place walls (2-3 random walls for hard+)
  const wallPositions = new Set<string>();
  if (tier.walls) {
    const wallCount = randInt(rng, 2, 3);
    for (let i = 0; i < wallCount; i++) {
      const pos: Pos = {
        row: randInt(rng, 0, rows - 1),
        col: randInt(rng, 0, cols - 1),
      };
      wallPositions.add(posKey(pos));
      board = setCell(board, pos, { kind: "wall" });
    }
  }

  // Step 2: Place all emitters randomly
  const allCells: Pos[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!wallPositions.has(posKey({ row: r, col: c }))) {
        allCells.push({ row: r, col: c });
      }
    }
  }

  const shuffledCells = shuffle(rng, allCells);
  if (shuffledCells.length < tier.emitterCount + tier.clueCount) return null;

  const solutionEmitters: PlacedEmitter[] = [];
  const emitterPosSet = new Set<string>();

  for (let i = 0; i < tier.emitterCount; i++) {
    const pos = shuffledCells[i]!;
    const shape = randPick(rng, tier.shapes);
    const range = randInt(rng, tier.minRange, tier.maxRange);
    const rotation = randInt(rng, 0, distinctRotations(shape) - 1);

    const emitter: PlacedEmitter = {
      id: nextId("e"),
      type: { shape, range },
      pos,
      rotation,
      fixed: false,
    };
    solutionEmitters.push(emitter);
    emitterPosSet.add(posKey(pos));
  }

  // Step 3: Compute signal map
  const signalMap = computeSignalMap(board, solutionEmitters);

  // Step 4: Select clue cells — prefer cells with signal >= 1
  const candidateClues: Pos[] = [];
  const zeroSignalCells: Pos[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const p = { row: r, col: c };
      const pk = posKey(p);
      if (wallPositions.has(pk) || emitterPosSet.has(pk)) continue;
      const sig = signalMap[r]![c]!;
      if (sig >= 1) {
        candidateClues.push(p);
      } else {
        zeroSignalCells.push(p);
      }
    }
  }

  const shuffledCandidates = shuffle(rng, candidateClues);
  // Add some 0-signal clues for harder puzzles
  const shuffledZeros = shuffle(rng, zeroSignalCells);
  const zeroCount = Math.min(Math.floor(tier.clueCount * 0.2), shuffledZeros.length);
  const selectedClues: Pos[] = [
    ...shuffledCandidates.slice(0, tier.clueCount - zeroCount),
    ...shuffledZeros.slice(0, zeroCount),
  ];

  if (selectedClues.length < Math.max(3, Math.floor(tier.clueCount * 0.6))) return null;

  // Place clues on board
  for (const pos of selectedClues) {
    const sig = signalMap[pos.row]![pos.col]!;
    board = setCell(board, pos, { kind: "clue", clueValue: sig });
  }

  // Step 5: Partition into fixed and inventory
  const fixedCount = Math.max(1, Math.round(tier.emitterCount * tier.fixedRatio));
  const shuffledEmitters = shuffle(rng, solutionEmitters);

  const fixedEmitters: PlacedEmitter[] = shuffledEmitters
    .slice(0, fixedCount)
    .map((e) => ({ ...e, fixed: true }));

  const inventoryEmitters: InventoryEmitter[] = shuffledEmitters
    .slice(fixedCount)
    .map((e) => ({
      id: e.id,
      type: e.type,
      rotation: 0, // reset rotation — player must figure it out
    }));

  // Mark emitter cells as empty (they shouldn't become clues)
  // Fixed emitters stay on board, inventory slots are just empty cells

  // Step 6: Verify unique solution
  const isUnique = verifyOrFix(board, fixedEmitters, inventoryEmitters, shuffledEmitters, fixedCount, rng);
  if (!isUnique) return null;

  return {
    id: "",
    board,
    fixedEmitters: isUnique.fixed,
    inventory: isUnique.inventory,
    difficulty: tier,
    totalClues: selectedClues.length,
  };
}

function verifyOrFix(
  board: Board,
  fixedEmitters: PlacedEmitter[],
  inventoryEmitters: InventoryEmitter[],
  allEmitters: PlacedEmitter[],
  fixedCount: number,
  rng: () => number
): { fixed: PlacedEmitter[]; inventory: InventoryEmitter[] } | null {
  let fixed = [...fixedEmitters];
  let inventory = [...inventoryEmitters];

  // Try up to 3 times: promote an inventory emitter to fixed if not unique
  for (let tries = 0; tries < 3; tries++) {
    const solutions = findSolutions(board, fixed, inventory, 2, 3000);

    if (solutions.length === 1) {
      return { fixed, inventory };
    }

    if (solutions.length === 0) return null; // unsolvable

    // Not unique — promote one inventory emitter to fixed
    if (inventory.length <= 1) return null; // can't promote further

    // Find the emitter from inventory and get its solution placement
    const promoteId = inventory[0]!.id;
    const solutionEmitter = allEmitters.find((e) => e.id === promoteId);
    if (!solutionEmitter) return null;

    fixed = [...fixed, { ...solutionEmitter, fixed: true }];
    inventory = inventory.slice(1);
  }

  // Final check
  const solutions = findSolutions(board, fixed, inventory, 2, 3000);
  if (solutions.length === 1) return { fixed, inventory };
  return null;
}

function generateFallbackPuzzle(rng: () => number, tier: DifficultyTier): Puzzle {
  // Simple 3x3 with one emitter
  let board = createBoard(3, 3);

  const emitter: PlacedEmitter = {
    id: nextId("e"),
    type: { shape: "single", range: 2 },
    pos: { row: 1, col: 0 },
    rotation: 1, // right
    fixed: false,
  };

  // Signal: (1,1) = 1, (1,2) = 1
  board = setCell(board, { row: 1, col: 1 }, { kind: "clue", clueValue: 1 });
  board = setCell(board, { row: 1, col: 2 }, { kind: "clue", clueValue: 1 });
  board = setCell(board, { row: 0, col: 0 }, { kind: "clue", clueValue: 0 });

  return {
    id: "",
    board,
    fixedEmitters: [],
    inventory: [{
      id: emitter.id,
      type: emitter.type,
      rotation: 0,
    }],
    difficulty: tier,
    totalClues: 3,
  };
}
